import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useApp } from '../../context/AppContext';
import { Card, StatusBadge, KnockoutBracket, RoundRobinTable, Modal, ConfirmModal, ScoreDisplay, Ico, Icons, resolveTeam, PlayerAvatar, ManageTournamentSkeleton, MatchUpdateForm } from '../../components/common';
import { tournamentAPI, playerAPI, matchAPI } from '../../services/api';
import { groupStageAPI } from '../../services/groupStageAPI';

export default function ManageTournament() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { generateKnockoutBracket, generateRoundRobinFixtures, generateAIDraw, updateScore, updateMatch, updateTournament, getRoundRobinStandings, currentUser, isAdvanced, addCoupon, deleteCoupon, checkInPlayer, checkOutPlayer, addNotification, resolveMatchSpecial, addAnnouncement, deleteAnnouncement, addDiscussionMessage, joinTournament, leaveTournament, bulkAddPlayersToTournament, setTournamentSeeds, resetTournament, completeTournament } = useApp();

  const [ready, setReady] = useState(false);
  const [tournament, setTournament] = useState(null);
  const [matches, setMatches] = useState([]);
  const [players, setPlayers] = useState([]);
  const [allPlayers, setAllPlayers] = useState([]);
  const [allPlayersLoaded, setAllPlayersLoaded] = useState(false);
  const [loadingAllPlayers, setLoadingAllPlayers] = useState(false);

  const load = async () => {
    try {
      const detailsRes = await tournamentAPI.getDetails(id);
      setTournament(detailsRes.data.tournament);
      
      // Check if this is a group stage tournament
      const gsRes = await groupStageAPI.getGroupStageForTournament(id).catch(() => null);
      if (gsRes?.data) {
        // Redirect to group stage view
        navigate(`/organizer/tournament-group-stage/${id}`, { replace: true });
        return;
      }
      
      setMatches(detailsRes.data.matches);
      setPlayers(detailsRes.data.players);
      setReady(true);
    } catch (err) {
      console.error(err);
      setReady(true);
    }
  };

  const loadAllPlayers = async () => {
    console.log('Loading all players...');
    setLoadingAllPlayers(true);
    try {
      const allPlayersRes = await playerAPI.getAll('limit=10000&sort=name');
      console.log('All players response:', allPlayersRes);
      setAllPlayers(allPlayersRes.data || []);
      setAllPlayersLoaded(true);
      console.log('All players loaded:', allPlayersRes.data?.length || 0);
    } catch (err) {
      console.error('Error loading all players:', err);
    } finally {
      setLoadingAllPlayers(false);
    }
  };

  useEffect(() => {
    load();
  }, [id]);
  
  // Listen for close all modals event (triggered when exiting fullscreen)
  useEffect(() => {
    const handleCloseAllModals = () => {
      setScoreModal(null);
      setCompleteModal(false);
      setResetModal(false);
      setDeleteConfirm(null);
      setRemovePlayerModal(null);
      setReplacePlayerModal(null);
      setPointsResult(null);
    };
    
    document.addEventListener('closeAllModals', handleCloseAllModals);
    return () => document.removeEventListener('closeAllModals', handleCloseAllModals);
  }, []);
  const [generatingAI, setGeneratingAI] = useState(false);
  const [resetModal, setResetModal] = useState(false);
  const [resetFlags, setResetFlags] = useState({ removePlayers: false, removeMatches: true, resetBracketGeneration: true, resetSeeds: false });
  const [completeModal, setCompleteModal] = useState(false);
  const [pointsResult, setPointsResult] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [scoreModal, setScoreModal] = useState(null);
  const [scoreForm, setScoreForm] = useState({ sets: [], status: 'live', winner: '', date: '', time: '', court: '' });
  const [tab, setTab] = useState(null);
  const [drawFilter, setDrawFilter] = useState('all');
  const [scoresFilter, setScoresFilter] = useState('all');
  const [couponForm, setCouponForm] = useState({ code: '', discount: '', type: 'percent', maxUses: 10 });
  const [notifMsg, setNotifMsg] = useState('');
  const [annTitle, setAnnTitle] = useState('');
  const [annMsg, setAnnMsg] = useState('');
  const [chatMsg, setChatMsg] = useState('');
  const [bulkText, setBulkText] = useState('');
  const [bulkResult, setBulkResult] = useState(null);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [standings, setStandings] = useState([]);
  const [playerSearch, setPlayerSearch] = useState('');
  const [selectedPlayers, setSelectedPlayers] = useState([]);
  const [addingPlayers, setAddingPlayers] = useState(false);
  const [addPlayerError, setAddPlayerError] = useState('');
  const [removePlayerModal, setRemovePlayerModal] = useState(null);
  const [replacePlayerModal, setReplacePlayerModal] = useState(null);
  const [replaceSearch, setReplaceSearch] = useState('');
  const [replacingPlayer, setReplacingPlayer] = useState(false);
  const [regSubTab, setRegSubTab] = useState('players');
  const [savingScore, setSavingScore] = useState(false);
  const [scoreError, setScoreError] = useState('');
  const [flashCell, setFlashCell] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [expandedRounds, setExpandedRounds] = useState({});
  const [matchPlayerSearch, setMatchPlayerSearch] = useState('');
  const [playersTabSearch, setPlayersTabSearch] = useState('');
  const [seedsTabSearch, setSeedsTabSearch] = useState('');
  const tapTimer = React.useRef(null);
  const quickUpdate = async (m, setIdx, pKey, delta) => {
    const sets = m.score.sets.map(s => ({ ...s }));
    sets[setIdx][pKey] = Math.max(0, Math.min(99, (sets[setIdx][pKey] || 0) + delta));
    const cellId = `${m.id}-${setIdx}-${pKey}`;
    setFlashCell({ id: cellId, color: delta > 0 ? '#43A047' : '#E53935' });
    setTimeout(() => setFlashCell(null), 400);
    try {
      await updateScore(m.id, { sets }, 'live', null);
      await load();
    } catch (e) { console.error(e); }
  };

  const t = tournament;
  const isKO = t ? t.type === 'Knockout' : true;

  useEffect(() => {
    console.log('regSubTab changed to:', regSubTab, 'allPlayersLoaded:', allPlayersLoaded);
    if (regSubTab === 'add' && !allPlayersLoaded) {
      loadAllPlayers();
    }
  }, [regSubTab]);

  useEffect(() => {
    if (t && !isKO && t.id) { getRoundRobinStandings(t.id).then(d => setStandings(d || [])); }
  }, [t?.id, isKO, getRoundRobinStandings]);

  // Reset tab to registrations when draw is not generated
  useEffect(() => {
    if (t && !t.bracketGenerated && tab && (tab === 'bracket' || tab === 'standings')) {
      setTab('registrations');
    }
  }, [t?.bracketGenerated]);

  // Auto-expand all rounds when matches tab is active
  useEffect(() => {
    if (matches.length > 0) {
      const rounds = [...new Set(matches.map(m => m.round))];
      const initialExpanded = {};
      rounds.forEach(round => { initialExpanded[round] = true; });
      setExpandedRounds(prev => {
        // Only update if different to avoid infinite loops
        const hasChanges = rounds.some(r => !prev[r]);
        return hasChanges ? initialExpanded : prev;
      });
    }
  }, [matches.length, scoresFilter, matchPlayerSearch]);

  if (!ready) return <ManageTournamentSkeleton />;
  if (!t) return <div style={s.container}><h2>Tournament not found</h2></div>;
  if (t.organizerId !== currentUser?.id) return <div style={s.container}><h2>Access denied</h2></div>;

  const tM = matches;
  const isDoubles = t.matchType === 'Doubles';
  const reg = t.registeredPlayers.map(rp => resolveTeam(rp, players));
  const defaultTab = !t.bracketGenerated ? 'registrations' : (isKO ? 'bracket' : 'standings');
  const activeTab = tab || defaultTab;
  const canGen = !t.bracketGenerated && t.registeredPlayers.length >= 2;
  const isDone = t.status === 'completed';
  const koReady = true; // Always ready for knockout if we have at least 2 players

  const handlePlayerSwap = async (match1Id, slot1, match2Id, slot2) => {
    try {
      await matchAPI.swapPlayers({ match1Id, slot1, match2Id, slot2 });
      await load();
    } catch (err) {
      console.error('Swap failed:', err);
      alert(err.message || 'Failed to swap players');
    }
  };

  const handleMatchSwap = async (match1Id, match2Id) => {
    try {
      await matchAPI.swapMatches({ match1Id, match2Id });
      await load();
    } catch (err) {
      console.error('Match swap failed:', err);
      alert(err.message || 'Failed to swap matches');
    }
  };

  const handleReplacePlayer = async (newPlayerId) => {
    if (!replacePlayerModal || !newPlayerId) return;
    setReplacingPlayer(true);
    try {
      const oldPlayerId = replacePlayerModal.rpId;
      
      // Check if old player has a seed
      const oldSeed = (t.seeds || []).find(sd => sd.playerId === oldPlayerId);
      
      // Remove old player and add new player
      await leaveTournament(id, oldPlayerId.includes('+') ? oldPlayerId.split('+')[0] : oldPlayerId);
      await joinTournament(id, newPlayerId);
      
      // If old player had a seed, transfer it to new player
      if (oldSeed) {
        const updatedSeeds = (t.seeds || [])
          .filter(sd => sd.playerId !== oldPlayerId)
          .concat({ playerId: newPlayerId, seedNumber: oldSeed.seedNumber });
        await setTournamentSeeds(t.id, updatedSeeds);
      }
      
      await load();
      setReplacePlayerModal(null);
      setReplaceSearch('');
    } catch (err) {
      console.error('Replace failed:', err);
      alert(err.message || 'Failed to replace player');
    } finally {
      setReplacingPlayer(false);
    }
  };

  const openScore = m => {
    // Set default date and time if missing
    const defaultDate = m.date || new Date().toISOString().split('T')[0]; // YYYY-MM-DD
    const now = new Date();
    const defaultTime = m.time || `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`; // HH:MM
    
    // Only change status to 'live' if both players are present
    const hasBothPlayers = m.player1 && m.player2;
    const defaultStatus = hasBothPlayers && m.status === 'scheduled' ? 'live' : m.status;
    
    setScoreModal(m);
    setScoreForm({ 
      sets: m.score.sets.length ? m.score.sets.map(s => ({ ...s })) : [{ p1: 0, p2: 0 }], 
      status: defaultStatus, 
      winner: m.winner || '', 
      date: defaultDate, 
      time: defaultTime, 
      court: m.court || '', 
      specialResult: m.specialResult || '' 
    });
    setScoreError('');
  };
  const saveScore = async () => {
    // Clear previous errors
    setScoreError('');
    
    // Validation: Winner is required when status is completed and no special result
    if (scoreForm.status === 'completed' && !scoreForm.specialResult && !scoreForm.winner) {
      setScoreError('Please select a winner for completed match');
      return;
    }
    
    // Validation: Winner is required for special results
    if (scoreForm.specialResult && !scoreForm.winner) {
      setScoreError('Please select a winner for the special result');
      return;
    }
    
    setSavingScore(true);
    try {
      if (scoreForm.specialResult && scoreForm.winner) {
        await resolveMatchSpecial(scoreModal.id, scoreForm.winner, scoreForm.specialResult);
      } else {
        await updateScore(scoreModal.id, { sets: scoreForm.sets }, scoreForm.status, scoreForm.winner || null);
      }
      await updateMatch(scoreModal.id, { date: scoreForm.date, time: scoreForm.time, court: scoreForm.court });
      if (!isKO && scoreForm.winner) {
        getRoundRobinStandings(id).then(d => setStandings(d || []));
      }
      setScoreModal(null);
      // Reload tournament data after score update
      await load();
    } finally {
      setSavingScore(false);
    }
  };
  const addSet = () => setScoreForm(p => ({ ...p, sets: [...p.sets, { p1: 0, p2: 0 }] }));
  const removeSet = () => setScoreForm(p => ({ ...p, sets: p.sets.slice(0, -1) }));
  const incSet = (i, w) => { const sets = [...scoreForm.sets]; sets[i] = { ...sets[i], [w]: Math.min(99, (sets[i][w] || 0) + 1) }; setScoreForm(p => ({ ...p, sets })); };
  const decSet = (i, w) => { const sets = [...scoreForm.sets]; sets[i] = { ...sets[i], [w]: Math.max(0, (sets[i][w] || 0) - 1) }; setScoreForm(p => ({ ...p, sets })); };
  const updSet = (i, w, v) => { const sets = [...scoreForm.sets]; sets[i] = { ...sets[i], [w]: parseInt(v) || 0 }; setScoreForm(p => ({ ...p, sets })); };

  const handleBulkUpload = async () => {
    const lines = bulkText.trim().split('\n').filter(l => l.trim());
    if (lines.length === 0) return;

    setBulkLoading(true);
    try {
      const result = await bulkAddPlayersToTournament(id, lines);
      if (result) {
        setBulkResult(result);
        setBulkText('');
        // Reload tournament data after bulk add
        await load();
      }
    } catch (e) {
      console.error(e);
    } finally {
      setBulkLoading(false);
    }
  };

  const handleAddCoupon = e => {
    e.preventDefault();
    if (!couponForm.code.trim() || !couponForm.discount) return;
    addCoupon(id, { code: couponForm.code.toUpperCase(), discount: parseInt(couponForm.discount), type: couponForm.type, maxUses: parseInt(couponForm.maxUses) || 10 });
    setCouponForm({ code: '', discount: '', type: 'percent', maxUses: 10 });
  };

  const tabs = isKO
    ? [
        ...(t.bracketGenerated ? [{ key: 'bracket', label: 'Draw' }] : []),
        ...(t.bracketGenerated ? [{ key: 'matches', label: 'Scores' }] : []),
        { key: 'registrations', label: 'Players & Fees' },
        { key: 'info', label: 'Info' },
        { key: 'discussion', label: 'Discussion' },
        { key: 'coupons', label: 'Coupons' }
      ]
    : [
        ...(t.bracketGenerated ? [{ key: 'standings', label: 'Standings' }] : []),
        ...(t.bracketGenerated ? [{ key: 'matches', label: 'Scores' }] : []),
        { key: 'registrations', label: 'Players & Fees' },
        { key: 'info', label: 'Info' },
        { key: 'discussion', label: 'Discussion' },
        { key: 'coupons', label: 'Coupons' }
      ];

  const surfaceColors = { 'Hard Court': '#1E88E5', 'Clay': '#ffb68f', 'Grass': '#43A047' };
  const surfaceColor = surfaceColors[t.surface] || '#8892A6';
  const fillPct = (t.registeredPlayers.length / t.maxPlayers) * 100;
  const liveCount = tM.filter(m => m.status === 'live').length;
  const doneCount = tM.filter(m => m.status === 'completed').length;

  return (
    <div className="container">
      {/* Full-screen AI Loading Overlay */}
      {generatingAI && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 32, 91, 0.95)',
          backdropFilter: 'blur(8px)',
          zIndex: 9998,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          animation: 'fadeIn 0.3s ease'
        }}>
          <div style={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            border: '4px solid rgba(189, 255, 0, 0.2)',
            borderTopColor: '#BDFF00',
            animation: 'spin 1s linear infinite',
            marginBottom: 24
          }} />
          <h2 style={{
            color: '#BDFF00',
            fontSize: 24,
            fontWeight: 800,
            fontFamily: "'Outfit', sans-serif",
            marginBottom: 12,
            letterSpacing: 1
          }}>AI Generating Draw...</h2>
          <p style={{
            color: 'rgba(255, 255, 255, 0.8)',
            fontSize: 14,
            maxWidth: 400,
            textAlign: 'center',
            lineHeight: 1.6
          }}>Our AI is analyzing player rankings, seedings, and tournament structure to create the optimal draw. This may take a few moments.</p>
        </div>
      )}
      
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div onClick={() => navigate('/organizer')} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer', color: '#00205B', fontWeight: 600, fontSize: 14 }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
          Back
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <StatusBadge status={t.status} />
          {liveCount > 0 && <span style={s.liveTag}>● {liveCount} LIVE</span>}
          {!isDone && (
            <div className="home-mobile-only" style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
              <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--gray-500)' }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <circle cx="12" cy="12" r="2"/><circle cx="12" cy="5" r="2"/><circle cx="12" cy="19" r="2"/>
                </svg>
              </button>
              {mobileMenuOpen && (
                <>
                  <div onClick={() => setMobileMenuOpen(false)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 999 }} />
                  <div style={{ position: 'absolute', top: '100%', right: 0, marginTop: 4, background: 'var(--card-bg)', border: '1.5px solid var(--card-border)', borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', minWidth: 200, zIndex: 1000 }}>
                    {canGen && koReady && (
                      <button onClick={async () => { setMobileMenuOpen(false); if (isKO && isAdvanced) { setGeneratingAI(true); try { await generateAIDraw(id); await load(); } catch(e) { alert(e.message || 'AI draw failed'); } finally { setGeneratingAI(false); } } else { isKO ? await generateKnockoutBracket(id) : await generateRoundRobinFixtures(id); await load(); } }} style={{ width: '100%', padding: '12px 16px', background: 'none', border: 'none', textAlign: 'left', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 8, borderBottom: '1px solid var(--card-border)' }}>
                        <Ico d={isKO && isAdvanced ? Icons.aiStars : Icons.plus} size={16} color="var(--text-primary)" />
                        {isKO && isAdvanced ? (generatingAI ? 'AI Generating...' : 'Generate Draw with AI') : `Generate ${isKO ? 'Draw' : 'Fixtures'}`}
                      </button>
                    )}
                    {t.status === 'ongoing' && (
                      <button onClick={() => { setMobileMenuOpen(false); setCompleteModal(true); }} style={{ width: '100%', padding: '12px 16px', background: 'none', border: 'none', textAlign: 'left', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#43A047', display: 'flex', alignItems: 'center', gap: 8, borderBottom: '1px solid var(--card-border)' }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#43A047" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="12" cy="12" r="10"/>
                          <path d="M9 12l2 2 4-4"/>
                        </svg>
                        Mark as Complete
                      </button>
                    )}
                    <button onClick={() => { setMobileMenuOpen(false); setResetModal(true); }} style={{ width: '100%', padding: '12px 16px', background: 'none', border: 'none', textAlign: 'left', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#E53935', display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 16 }}>↻</span>
                      Reset Tournament
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1, minWidth: 0 }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
              <h1 className="heading" style={{ margin: 0, fontSize: 18, wordBreak: 'break-word', minWidth: 0 }}>{t.name}</h1>
            </div>
            <div style={{ display: 'flex', gap: 12, fontSize: 11, color: 'var(--gray-400)', flexWrap: 'wrap', wordBreak: 'break-word' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Ico d={Icons.calendar} size={10} color="var(--gray-400)" /> {new Date(t.startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
              <span>{t.registeredPlayers.length}/{t.maxPlayers} players</span>
            </div>
          </div>
        </div>
        {!isDone && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
            {/* Desktop buttons - all visible */}
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }} className="home-desktop-only">
              {canGen && koReady && (
                isKO && isAdvanced
                  ? <button disabled={generatingAI} onClick={async () => { setGeneratingAI(true); try { await generateAIDraw(id); await load(); } catch(e) { alert(e.message || 'AI draw failed'); } finally { setGeneratingAI(false); } }} style={s.genBtn}><Ico d={Icons.aiStars} size={14} color="#BDFF00" /> {generatingAI ? 'AI Generating...' : 'Generate Draw with AI'}</button>
                  : <button onClick={async () => { isKO ? await generateKnockoutBracket(id) : await generateRoundRobinFixtures(id); await load(); }} style={s.genBtn}>Generate {isKO ? 'Draw' : 'Fixtures'}</button>
              )}
              {t.status === 'ongoing' && <button onClick={() => setCompleteModal(true)} style={s.completeBtn}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="12" cy="12" r="10"/>
                          <path d="M9 12l2 2 4-4"/>
                        </svg>
                        Mark as Complete
                      </button>}
              <button onClick={() => setResetModal(true)} style={s.resetBtn}>↻ Reset</button>
            </div>
            {/* Mobile - 3-dot menu */}
            <div className="home-mobile-only" style={{ position: 'relative', display: 'flex', gap: 12, alignItems: 'center' }}>
            </div>
          </div>
        )}
      </div>

      <div className="tabs">{tabs.map(tb => <button key={tb.key} onClick={() => setTab(tb.key)} className={`tab ${activeTab === tb.key ? 'tab-active' : ''}`}>{tb.label}</button>)}</div>

      <div style={{ marginTop: 16, animation: 'fadeIn 0.3s ease' }}>
        {activeTab === 'bracket' && isKO && (t.bracketGenerated ? <>
          <KnockoutBracket matches={tM} players={players} seeds={t.seeds || []} onMatchClick={!isDone ? openScore : undefined} onPlayerSwap={isAdvanced ? handlePlayerSwap : undefined} onMatchSwap={isAdvanced ? handleMatchSwap : undefined} editable={!isDone} tournamentName={t.name} onRefresh={load} isDoubles={isDoubles} />
          {isDone && <div style={{ textAlign: 'center', padding: '12px 0', fontSize: 12, color: '#8892A6', fontWeight: 600 }}>Tournament completed — editing disabled</div>}
        </> : <Card style={{ textAlign: 'center', padding: 48 }}><h3 style={{ color: '#00205B', fontFamily: "'Outfit', sans-serif" }}>Generate the Draw</h3><p style={{ color: '#8892A6', fontSize: 13, marginTop: 4 }}>{koReady ? 'Click "Generate Draw" above.' : 'Need correct player count.'}</p></Card>)}
        {activeTab === 'standings' && !isKO && (t.bracketGenerated ? <>
          <RoundRobinTable standings={standings} players={players} />
          <h3 style={{ fontSize: 13, color: '#00205B', fontFamily: "'Outfit', sans-serif", margin: '20px 0 10px', fontWeight: 800, letterSpacing: 1 }}>FIXTURES ({tM.length})</h3>
          <div className="org-draw-list">{tM.map(m => {
            const mDone = m.status === 'completed';
            const isBye = mDone && (!m.player1 || !m.player2);
            const p1 = m.player1 ? resolveTeam(m.player1, players) : { name: isBye ? 'Bye' : 'TBD' };
            const p2 = m.player2 ? resolveTeam(m.player2, players) : { name: isBye ? 'Bye' : 'TBD' };
            return (
              <div key={m.id} className="org-draw-item" onClick={() => openScore(m)}>
                <div className="org-draw-round">{m.round}</div>
                <div className="org-draw-players">
                  <span style={{ fontWeight: m.winner === m.player1 ? 700 : 500, color: m.winner === m.player1 ? '#00205B' : mDone && m.winner !== m.player1 ? '#B0B8C9' : '#3D4759' }}>{p1.name}</span>
                  <span style={{ fontSize: 10, color: '#B0B8C9' }}>vs</span>
                  <span style={{ fontWeight: m.winner === m.player2 ? 700 : 500, color: m.winner === m.player2 ? '#00205B' : mDone && m.winner !== m.player2 ? '#B0B8C9' : '#3D4759' }}>{p2.name}</span>
                </div>
                <div className="org-draw-meta">
                  <StatusBadge status={m.status} />
                  {m.court && <span className="chip-sm">{m.court}</span>}
                </div>
                <Ico d={Icons.chevron} size={14} color="#B0B8C9" />
              </div>
            );
          })}</div>
        </> : <Card style={{ textAlign: 'center', padding: 48 }}><h3 style={{ color: '#00205B' }}>Generate Fixtures First</h3></Card>)}
        {activeTab === 'matches' && (tM.length === 0 ? <Card><p style={{ color: '#8892A6', textAlign: 'center' }}>Generate {isKO ? 'draw' : 'fixtures'} first</p></Card> : (
          <div>
            {/* Player Search */}
            <div style={{ marginBottom: 14, position: 'relative' }}>
              <svg style={{ position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#8892A6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8"/>
                <path d="m21 21-4.35-4.35"/>
              </svg>
              <input
                className="form-input"
                placeholder="Search by player name..."
                value={matchPlayerSearch}
                onChange={e => setMatchPlayerSearch(e.target.value)}
                style={{ width: '100%', padding: '12px 0 12px 32px', fontSize: 14, border: 0, borderBottom: '1px solid #E5E8EF', borderRadius: 0, background: 'transparent', transition: 'border-color 0.2s', outline: 0, boxShadow: 'none' }}
                onFocus={e => { e.target.style.borderBottom = '2px solid #00205B'; e.target.style.outline = 'none'; e.target.style.boxShadow = 'none'; }}
                onBlur={e => { e.target.style.borderBottom = '1px solid #E5E8EF'; e.target.style.boxShadow = 'none'; }}
              />
            </div>
            {/* Scores filter pills */}
            {(() => {
              // Filter out bye matches
              const nonByeMatches = tM.filter(m => m.player1 && m.player2);
              
              const liveM = nonByeMatches.filter(m => m.status === 'live');
              const upcomingM = nonByeMatches.filter(m => m.status === 'scheduled' || m.status === 'pending').sort((a, b) => {
                const getDateTime = (match) => {
                  if (!match.date) return new Date('9999-12-31').getTime();
                  const dateTimeStr = match.date + 'T' + (match.time || '00:00');
                  return new Date(dateTimeStr).getTime();
                };
                return getDateTime(a) - getDateTime(b);
              });
              const completedM = nonByeMatches.filter(m => m.status === 'completed').sort((a, b) => {
                const getDateTime = (match) => {
                  if (!match.date) return 0;
                  const dateTimeStr = match.date + 'T' + (match.time || '00:00');
                  return new Date(dateTimeStr).getTime();
                };
                return getDateTime(b) - getDateTime(a);
              });
              
              const sortedAllMatches = [
                ...liveM,
                ...upcomingM,
                ...completedM
              ];
              
              // Apply player search filter
              const searchLower = matchPlayerSearch.toLowerCase();
              const matchesPlayerSearch = (m) => {
                if (!searchLower) return true;
                const t1 = m.player1 ? resolveTeam(m.player1, players) : { name: '' };
                const t2 = m.player2 ? resolveTeam(m.player2, players) : { name: '' };
                return t1.name.toLowerCase().includes(searchLower) || t2.name.toLowerCase().includes(searchLower);
              };
              
              const filteredByStatus = scoresFilter === 'live' ? liveM : scoresFilter === 'upcoming' ? upcomingM : scoresFilter === 'completed' ? completedM : sortedAllMatches;
              const filteredScores = filteredByStatus.filter(matchesPlayerSearch);
              const scoreTabs = [
                { key: 'all', label: 'All', count: sortedAllMatches.filter(matchesPlayerSearch).length },
                { key: 'live', label: 'Live', count: liveM.filter(matchesPlayerSearch).length, dot: true },
                { key: 'upcoming', label: 'Upcoming', count: upcomingM.filter(matchesPlayerSearch).length },
                { key: 'completed', label: 'Completed', count: completedM.filter(matchesPlayerSearch).length },
              ];
              
              // Group matches by round
              const roundOrder = ['Round of 128', 'Round of 64', 'Round of 32', 'Round of 16', 'Quarter Final', 'Semi Final', 'Final'];
              const groupedByRound = {};
              filteredScores.forEach(m => {
                if (!groupedByRound[m.round]) groupedByRound[m.round] = [];
                groupedByRound[m.round].push(m);
              });
              const sortedRounds = Object.keys(groupedByRound).sort((a, b) => {
                const aIdx = roundOrder.indexOf(a);
                const bIdx = roundOrder.indexOf(b);
                if (aIdx !== -1 && bIdx !== -1) return aIdx - bIdx;
                if (aIdx !== -1) return -1;
                if (bIdx !== -1) return 1;
                return a.localeCompare(b);
              });
              
              return (
                <>
                  <div style={{ display: 'flex', gap: 6, marginBottom: 14, overflowX: 'auto', flexWrap: 'nowrap', WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none', msOverflowStyle: 'none' }} className="hide-scrollbar">
                    {scoreTabs.map(st => (
                      <button key={st.key} onClick={() => setScoresFilter(st.key)}
                        style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 14px', borderRadius: 20, border: `1.5px solid ${scoresFilter === st.key ? '#00205B' : '#EEF1F6'}`, background: scoresFilter === st.key ? '#00205B' : 'var(--card-bg)', color: scoresFilter === st.key ? '#BDFF00' : '#8892A6', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: "'Outfit', sans-serif", transition: 'all 0.2s', flexShrink: 0, whiteSpace: 'nowrap' }}>
                        {st.dot && st.count > 0 && <span style={{ width: 6, height: 6, borderRadius: '50%', background: scoresFilter === st.key ? '#BDFF00' : '#E53935', animation: 'pulse 1.5s infinite' }} />}
                        {st.label}
                        <span style={{ fontSize: 10, fontWeight: 700, background: scoresFilter === st.key ? 'rgba(189,255,0,0.2)' : '#EEF1F6', color: scoresFilter === st.key ? '#BDFF00' : '#8892A6', padding: '1px 6px', borderRadius: 10 }}>{st.count}</span>
                      </button>
                    ))}
                  </div>
                  
                  {/* Timeline View */}
                  {sortedRounds.length === 0 ? (
                    <Card><p style={{ color: '#8892A6', textAlign: 'center' }}>No matches found</p></Card>
                  ) : (
                    <div style={{ position: 'relative' }}>
                      {/* Vertical timeline line */}
                      <div style={{ position: 'absolute', left: 9, top: 0, bottom: 0, width: 2, background: 'linear-gradient(180deg, #00205B 0%, #1E88E5 100%)', zIndex: 0 }} />
                      
                      {sortedRounds.map((round, idx) => {
                        const roundMatches = groupedByRound[round];
                        const isExpanded = expandedRounds[round];
                        const isLastRound = idx === sortedRounds.length - 1;
                        return (
                          <div key={round} style={{ position: 'relative', paddingLeft: 28, marginBottom: idx < sortedRounds.length - 1 ? 20 : 0 }}>
                            {/* Tennis ball node */}
                            <div
                              onClick={() => setExpandedRounds(prev => ({ ...prev, [round]: !prev[round] }))}
                              style={{
                                position: 'absolute',
                                left: 0,
                                top: 0,
                                width: 18,
                                height: 18,
                                borderRadius: '50%',
                                background: isExpanded ? '#00205B' : '#8892A6',
                                border: '2px solid #fff',
                                boxShadow: isExpanded ? '0 0 0 3px rgba(0,32,91,0.2)' : 'none',
                                cursor: 'pointer',
                                zIndex: 1,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: 'all 0.2s'
                              }}
                            >
                              <svg width="10" height="10" viewBox="0 0 24 24" fill="none">
                                <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2z" stroke={isExpanded ? '#BDFF00' : '#fff'} strokeWidth="2" fill="none" />
                                <path d="M12 2c0 5.5-4.5 10-10 10" stroke={isExpanded ? '#BDFF00' : '#fff'} strokeWidth="1.5" fill="none" />
                                <path d="M12 22c0-5.5 4.5-10 10-10" stroke={isExpanded ? '#BDFF00' : '#fff'} strokeWidth="1.5" fill="none" />
                              </svg>
                            </div>
                            
                            {/* Round header */}
                            <div
                              onClick={() => setExpandedRounds(prev => ({ ...prev, [round]: !prev[round] }))}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                marginBottom: isExpanded ? 12 : 0,
                                cursor: 'pointer',
                                userSelect: 'none'
                              }}
                            >
                              <h3 style={{ fontSize: 11, fontWeight: 800, color: '#00205B', letterSpacing: 1.5, textTransform: 'uppercase', margin: 0, fontFamily: "'Outfit', sans-serif" }}>
                                {round}
                              </h3>
                              <span style={{ fontSize: 10, fontWeight: 700, background: '#EEF1F6', color: '#8892A6', padding: '2px 8px', borderRadius: 10 }}>
                                {roundMatches.length}
                              </span>
                            </div>
                            
                            {/* Matches grid */}
                            {isExpanded && (
                              <div className="grid-matches" style={{ marginTop: 0 }}>{roundMatches.map(m => {
                    const isLive = m.status === 'live';
                    const mDone = m.status === 'completed';
                    const isBye = mDone && (!m.player1 || !m.player2);
                    const t1 = m.player1 ? resolveTeam(m.player1, players) : { name: isBye ? 'Bye' : 'TBD' };
                    const t2 = m.player2 ? resolveTeam(m.player2, players) : { name: isBye ? 'Bye' : 'TBD' };
                    const p1obj = players.find(p => p.id === m.player1);
                    const p2obj = players.find(p => p.id === m.player2);
                    const hasBoth = m.player1 && m.player2;
                    const w1 = mDone && m.winner === m.player1;
                    const w2 = mDone && m.winner === m.player2;
                    const sets = m.score?.sets || [];
                    const isDoublesMatch = t1.name.includes(' / ') || t2.name.includes(' / ');
                    
                    const renderRow = (team, pobj, isWinner, isLoser, pKey) => {
                      const isDoublesTeam = team.name.includes(' / ');
                      const players = isDoublesTeam ? team.name.split(' / ') : [team.name];
                      
                      return (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', background: isWinner ? 'rgba(67,160,71,0.08)' : 'transparent', borderLeft: isWinner ? '3px solid #43A047' : '3px solid transparent' }}>
                          <PlayerAvatar name={team.name} photo={pobj?.photo} size={28} />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            {isDoublesTeam ? (
                              <div>
                                <div style={{ fontSize: 12, fontWeight: isWinner ? 700 : 500, color: isWinner ? '#43A047' : isLoser ? '#B0B8C9' : '#00205B', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {players[0]}
                                </div>
                                <div style={{ fontSize: 11, fontWeight: isWinner ? 600 : 400, color: isWinner ? '#43A047' : isLoser ? '#D8DDE8' : '#5F6B80', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {players[1]}
                                </div>
                              </div>
                            ) : (
                              <span style={{ fontSize: 12, fontWeight: isWinner ? 700 : 500, color: isWinner ? '#43A047' : isLoser ? '#B0B8C9' : '#00205B', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {team.name}
                              </span>
                            )}
                          </div>
                          {isWinner && (
                            <span style={{ fontSize: 8, fontWeight: 800, color: '#fff', background: '#43A047', padding: '2px 6px', borderRadius: 3, letterSpacing: 0.5 }}>WON</span>
                          )}
                          <div style={{ display: 'flex', gap: 3, flexShrink: 0, alignItems: 'center' }}>
                            {sets.map((sv, i) => {
                              const mine = sv[pKey], opp = sv[pKey === 'p1' ? 'p2' : 'p1'];
                              const won = mine > opp;
                              const cellId = `${m.id}-${i}-${pKey}`;
                              const isFlash = flashCell?.id === cellId;
                              const canTap = isLive && !isDone;
                              return (
                                <span key={i}
                                  onClick={canTap ? (e) => { e.stopPropagation(); if (tapTimer.current) { clearTimeout(tapTimer.current); tapTimer.current = null; quickUpdate(m, i, pKey, -1); } else { tapTimer.current = setTimeout(() => { tapTimer.current = null; quickUpdate(m, i, pKey, 1); }, 250); } } : undefined}
                                  onContextMenu={canTap ? (e) => { e.preventDefault(); } : undefined}
                                  style={{ width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: won ? 700 : 400, color: isFlash ? '#fff' : won ? '#43A047' : '#B0B8C9', background: isFlash ? flashCell.color : won ? 'rgba(67,160,71,0.1)' : 'transparent', borderRadius: 6, fontFamily: "'Outfit', sans-serif", cursor: canTap ? 'pointer' : 'default', transition: 'all 0.2s', userSelect: 'none' }}>{mine}</span>
                              );
                            })}
                          </div>
                        </div>
                      );
                    };
                    
                    return (
                      <div key={m.id} style={{ background: '#FFFFFF', borderRadius: 12, border: `2px solid ${isLive ? '#E53935' : mDone ? '#43A047' : '#00205B'}`, overflow: 'hidden', boxShadow: isLive ? '0 4px 12px rgba(229,57,53,0.15)' : mDone ? '0 2px 8px rgba(67,160,71,0.08)' : '0 2px 8px rgba(0,32,91,0.1)', transition: 'all 0.2s' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 10px', borderBottom: '1px solid #F4F6FA', background: isLive ? 'rgba(229,57,53,0.05)' : mDone ? 'rgba(67,160,71,0.03)' : 'rgba(0,32,91,0.03)' }}>
                          <span style={{ fontSize: 10, color: '#5F6B80', fontWeight: 600 }}>{m.round}</span>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto' }}>
                            {isLive && <span style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 9, fontWeight: 800, color: '#E53935', letterSpacing: 0.8, background: '#FFEBEE', padding: '2px 6px', borderRadius: 3 }}><span style={{ width: 5, height: 5, borderRadius: '50%', background: '#E53935', animation: 'pulse 1.5s infinite', display: 'inline-block' }} />LIVE</span>}
                            {mDone && <span style={{ fontSize: 9, fontWeight: 800, color: '#43A047', letterSpacing: 0.5, background: '#E8F5E9', padding: '2px 6px', borderRadius: 3 }}>COMPLETED</span>}
                            {!isLive && !mDone && <span style={{ fontSize: 9, fontWeight: 700, color: '#00205B', background: 'rgba(0,32,91,0.08)', padding: '2px 6px', borderRadius: 3 }}>UPCOMING</span>}
                          </div>
                        </div>
                        {sets.length > 0 && (
                          <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '4px 10px 0', gap: 3, alignItems: 'center' }}>
                            {isLive && !isDone && <span onClick={async (e) => { e.stopPropagation(); const newSets = [...sets.map(s => ({ ...s })), { p1: 0, p2: 0 }]; try { await updateScore(m.id, { sets: newSets }, 'live', null); await load(); } catch {} }} style={{ width: 22, height: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: '#B0B8C9', background: '#F4F6FA', borderRadius: 4, cursor: 'pointer', userSelect: 'none', border: '1px dashed #D8DDE8' }} title="Add Set">+</span>}
                            {sets.map((_, i) => <span key={i} style={{ width: 28, textAlign: 'center', fontSize: 8, fontWeight: 700, color: '#B0B8C9', letterSpacing: 0.5 }}>S{i + 1}</span>)}
                          </div>
                        )}
                        <div style={{ padding: '3px 0 3px' }}>
                          {renderRow(t1, p1obj, w1, w2, 'p1')}
                          <div style={{ height: 1, background: '#F4F6FA', margin: '0 10px' }} />
                          {renderRow(t2, p2obj, w2, w1, 'p2')}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 10px', borderTop: '1px solid #F4F6FA', background: '#FAFBFC' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
                            {m.date && (
                              <span style={{ fontSize: 10, color: '#8892A6', fontWeight: 600 }}>
                                {new Date(m.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                              </span>
                            )}
                            {m.time && (
                              <span style={{ fontSize: 10, color: '#8892A6', fontWeight: 600 }}>
                                {m.time}
                              </span>
                            )}
                            {m.court && (
                              <span style={{ fontSize: 10, color: '#8892A6', fontWeight: 600, background: '#EEF1F6', padding: '2px 6px', borderRadius: 4 }}>
                                {m.court}
                              </span>
                            )}
                          </div>
                        <button onClick={() => !isDone && !isBye && openScore(m)} disabled={isDone || isBye} style={{ padding: '6px 12px', background: isDone ? '#F8F9FC' : isBye ? '#FFF3E0' : isLive ? '#E53935' : mDone ? '#43A047' : '#00205B', color: isDone ? '#8892A6' : isBye ? '#F57C00' : '#FFFFFF', border: isDone ? '1px solid #D8DDE8' : isBye ? '1px solid #FFB74D' : 'none', borderRadius: 6, cursor: isDone || isBye ? 'not-allowed' : 'pointer', fontWeight: 700, fontSize: 10, letterSpacing: 0.5, fontFamily: "'Outfit', sans-serif", opacity: isDone ? 0.5 : 1, flexShrink: 0 }}>
                          {isDone ? <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5 }}><Ico d={Icons.lock} size={12} /> Locked</span> : isBye ? <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5 }}><Ico d={Icons.check} size={12} /> Bye</span> : !hasBoth ? <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5 }}><Ico d={Icons.clock} size={12} /> Schedule</span> : m.status === 'scheduled' ? <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5 }}><Ico d={Icons.play} size={12} /> Start</span> : isLive ? <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5 }}><Ico d={Icons.edit} size={12} /> Update</span> : <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5 }}><Ico d={Icons.eye} size={12} /> View</span>}
                        </button>
                        </div>
                      </div>
                    );
                  })}</div>
                            )}
                          </div>
                        );
                      })}
                      
                      {/* End ball icon */}
                      <div style={{
                        position: 'absolute',
                        left: 0,
                        bottom: -10,
                        width: 18,
                        height: 18,
                        borderRadius: '50%',
                        background: '#43A047',
                        border: '2px solid #fff',
                        boxShadow: '0 0 0 3px rgba(67,160,71,0.2)',
                        zIndex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none">
                          <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2z" stroke="#fff" strokeWidth="2" fill="none" />
                          <path d="M12 2c0 5.5-4.5 10-10 10" stroke="#fff" strokeWidth="1.5" fill="none" />
                          <path d="M12 22c0-5.5 4.5-10 10-10" stroke="#fff" strokeWidth="1.5" fill="none" />
                        </svg>
                      </div>
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        ))}

        {/* Check-in Tab */}
        {activeTab === 'checkin' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h3 style={{ fontSize: 13, color: '#00205B', fontFamily: "'Outfit', sans-serif", fontWeight: 800, letterSpacing: 1, margin: 0 }}>PLAYER CHECK-IN ({(t.checkedIn || []).length}/{t.registeredPlayers.length})</h3>
            </div>
            <div style={{ height: 6, background: '#EEF1F6', borderRadius: 3, marginBottom: 16 }}>
              <div style={{ height: '100%', borderRadius: 3, background: '#43A047', width: `${t.registeredPlayers.length ? ((t.checkedIn || []).length / t.registeredPlayers.length) * 100 : 0}%`, transition: 'width 0.3s' }} />
            </div>
            <div className="org-p-grid">
              {reg.map((team, i) => {
                const rpId = t.registeredPlayers[i];
                const isIn = (t.checkedIn || []).includes(rpId);
                return (
                  <Card key={i} style={{ padding: 12, display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div className="org-p-num" style={isIn ? { background: '#E8F5E9', color: '#43A047' } : {}}>{i + 1}</div>
                    <div style={{ flex: 1 }}><strong style={{ fontSize: 13, color: isIn ? '#43A047' : '#00205B' }}>{team.name}</strong>
                      {isIn && <div style={{ fontSize: 10, color: '#43A047' }}>✓ Checked in</div>}
                    </div>
                    <button onClick={() => isIn ? checkOutPlayer(id, rpId) : checkInPlayer(id, rpId)} style={{ padding: '6px 14px', fontSize: 11, fontWeight: 700, border: 'none', borderRadius: 6, cursor: 'pointer', fontFamily: "'Outfit', sans-serif", ...(isIn ? { background: '#FFEBEE', color: '#E53935' } : { background: '#E8F5E9', color: '#43A047' }) }}>
                      {isIn ? 'Undo' : 'Check In'}
                    </button>
                  </Card>
                );
              })}
              {reg.length === 0 && <Card><p style={{ color: '#8892A6', textAlign: 'center' }}>No players registered</p></Card>}
            </div>
          </div>
        )}

        {/* Discussion Tab */}
        {activeTab === 'discussion' && (
          <div>
            <form onSubmit={e => { e.preventDefault(); if (chatMsg.trim()) { addDiscussionMessage(id, chatMsg); setChatMsg(''); } }} style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
              <input className="form-input" style={{ flex: 1 }} placeholder="Write a message..." value={chatMsg} onChange={e => setChatMsg(e.target.value)} />
              <button type="submit" disabled={!chatMsg.trim()} style={{ ...s.genBtn, padding: '10px 18px', fontSize: 12, opacity: chatMsg.trim() ? 1 : 0.5 }}>Send</button>
            </form>
            {(t.discussion || []).length === 0 ? (
              <Card style={{ padding: '24px 16px', textAlign: 'center' }}>
                <p style={{ color: '#8892A6', fontSize: 13, margin: 0 }}>No messages yet</p>
              </Card>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {[...(t.discussion || [])].reverse().map(msg => (
                  <Card key={msg.id} style={{ padding: '10px 14px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4, flexWrap: 'wrap' }}>
                      <PlayerAvatar name={msg.userName} size={22} />
                      <span style={{ fontSize: 12, fontWeight: 700, color: '#00205B' }}>{msg.userName}</span>
                      {msg.role === 'organizer' && <span style={{ fontSize: 9, fontWeight: 700, color: '#fff', background: '#00205B', padding: '2px 7px', borderRadius: 10, letterSpacing: 0.3, display: 'flex', alignItems: 'center', gap: 3 }}><Ico d={Icons.megaphone} size={8} /> Organizer</span>}
                      <span style={{ fontSize: 9, color: '#B0B8C9', marginLeft: 'auto' }}>{new Date(msg.createdAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                    </div>
                    <p style={{ margin: 0, fontSize: 13, color: '#3D4759', lineHeight: 1.5, paddingLeft: 28 }}>{msg.text}</p>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Info & Rules Tab */}
        {activeTab === 'info' && (
          <div>
            {(t.announcements || []).length > 0 && <>
              <h3 style={{ fontSize: 13, color: '#00205B', fontFamily: "'Outfit', sans-serif", marginBottom: 10, fontWeight: 800, letterSpacing: 1 }}>ANNOUNCEMENTS ({(t.announcements || []).length})</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
                {[...(t.announcements || [])].reverse().map(a => (
                  <Card key={a.id} style={{ padding: '12px 16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: '#00205B', marginBottom: 2 }}>{a.title}</div>
                        <p style={{ fontSize: 13, color: '#3D4759', margin: '0 0 4px', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{a.message}</p>
                        <span style={{ fontSize: 10, color: '#B0B8C9' }}>{new Date(a.createdAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                      </div>
                      <button onClick={() => setDeleteConfirm({ type: 'announcement', id: a.id })} style={{ background: 'none', border: 'none', color: '#E53935', cursor: 'pointer', fontSize: 14, flexShrink: 0, padding: 0, display: 'flex', alignItems: 'center' }}><Ico d={Icons.xCircle} size={16} /></button>
                    </div>
                  </Card>
                ))}
              </div>
            </>}

            {t.rules && <Card style={{ marginBottom: 12 }}>
              <h3 style={{ margin: '0 0 8px', fontSize: 13, color: '#00205B', fontFamily: "'Outfit', sans-serif", display: 'flex', alignItems: 'center', gap: 6 }}><Ico d={Icons.clipboard} size={14} /> Tournament Rules</h3>
              <p style={{ fontSize: 13, color: '#3D4759', whiteSpace: 'pre-wrap', lineHeight: 1.6, margin: 0 }}>{t.rules}</p>
            </Card>}
            <Card style={{ marginBottom: 12 }}>
              <h3 style={{ margin: '0 0 10px', fontSize: 13, color: '#00205B', fontFamily: "'Outfit', sans-serif", display: 'flex', alignItems: 'center', gap: 6 }}><Ico d={Icons.pin} size={14} /> Venue & Contact</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {t.location && <div style={{ fontSize: 13, color: '#3D4759', display: 'flex', alignItems: 'center', gap: 6 }}><Ico d={Icons.pin} size={12} color="#5F6B80" /> {t.location}</div>}
                {t.venueMap && <a href={t.venueMap} target="_blank" rel="noopener noreferrer" style={{ fontSize: 13, color: '#1E88E5', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 6 }}><Ico d={Icons.map} size={12} /> Open Map / Directions</a>}
                {t.parking && <div style={{ fontSize: 13, color: '#3D4759', display: 'flex', alignItems: 'center', gap: 6 }}><Ico d={Icons.parking} size={12} color="#5F6B80" /> {t.parking}</div>}
                {t.contactPhone && <div style={{ fontSize: 13, color: '#3D4759', display: 'flex', alignItems: 'center', gap: 6 }}><Ico d={Icons.phone} size={12} color="#5F6B80" /> {t.contactPhone}</div>}
                {t.contactEmail && <div style={{ fontSize: 13, color: '#3D4759', display: 'flex', alignItems: 'center', gap: 6 }}><Ico d={Icons.mail} size={12} color="#5F6B80" /> {t.contactEmail}</div>}
                {!t.venueMap && !t.parking && !t.contactPhone && !t.contactEmail && <p style={{ color: '#8892A6', fontSize: 12 }}>No venue info added. Edit tournament to add.</p>}
              </div>
            </Card>
          </div>
        )}

        {/* Coupons Tab */}
        {activeTab === 'coupons' && (
          <div>
            <Card style={{ marginBottom: 16 }}>
              <h3 style={{ margin: '0 0 12px', fontSize: 14, color: '#00205B', fontFamily: "'Outfit', sans-serif" }}>Create Coupon</h3>
              <form onSubmit={handleAddCoupon}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
                  <div><label className="form-label">Code</label><input className="form-input" value={couponForm.code} onChange={e => setCouponForm(p => ({ ...p, code: e.target.value }))} placeholder="e.g. EARLY50" required /></div>
                  <div><label className="form-label">Discount Type</label><select className="form-input" value={couponForm.type} onChange={e => setCouponForm(p => ({ ...p, type: e.target.value }))}><option value="percent">Percentage (%)</option><option value="flat">Flat (₹)</option></select></div>
                  <div><label className="form-label">{couponForm.type === 'percent' ? 'Discount %' : 'Discount ₹'}</label><input className="form-input" type="number" min="1" value={couponForm.discount} onChange={e => setCouponForm(p => ({ ...p, discount: e.target.value }))} required /></div>
                  <div><label className="form-label">Max Uses</label><input className="form-input" type="number" min="1" value={couponForm.maxUses} onChange={e => setCouponForm(p => ({ ...p, maxUses: e.target.value }))} /></div>
                </div>
                <button type="submit" disabled={isDone} style={{ ...s.genBtn, padding: '8px 20px', fontSize: 11, ...(isDone ? { opacity: 0.5, cursor: 'not-allowed' } : {}) }}>+ Add Coupon</button>
              </form>
            </Card>

            <h3 style={{ fontSize: 13, color: '#00205B', fontFamily: "'Outfit', sans-serif", marginBottom: 10, fontWeight: 800, letterSpacing: 1 }}>ACTIVE COUPONS ({(t.coupons || []).length})</h3>
            {(t.coupons || []).length === 0 ? (
              <Card><p style={{ color: '#8892A6', textAlign: 'center' }}>No coupons created yet</p></Card>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 10 }}>
                {(t.coupons || []).map(c => (
                  <Card key={c.id} style={{ padding: 14 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                      <span style={{ fontWeight: 800, color: '#00205B', fontFamily: "'Outfit', sans-serif", fontSize: 15, letterSpacing: 1 }}>{c.code}</span>
                      {!isDone && <button onClick={() => setDeleteConfirm({ type: 'coupon', id: c.id })} style={{ background: 'none', border: 'none', color: '#E53935', cursor: 'pointer', fontSize: 14, padding: 0, display: 'flex', alignItems: 'center' }}><Ico d={Icons.xCircle} size={16} /></button>}
                    </div>
                    <div style={{ fontSize: 12, color: '#5F6B80' }}>
                      {c.type === 'percent' ? `${c.discount}% off` : `₹${c.discount} off`}
                    </div>
                    <div style={{ fontSize: 11, color: '#8892A6', marginTop: 4 }}>
                      Used: {c.usedCount}/{c.maxUses}
                    </div>
                    <div style={{ height: 3, background: '#EEF1F6', borderRadius: 2, marginTop: 6 }}>
                      <div style={{ height: '100%', background: c.usedCount >= c.maxUses ? '#E53935' : '#43A047', borderRadius: 2, width: `${(c.usedCount / c.maxUses) * 100}%` }} />
                    </div>
                    {c.usedCount >= c.maxUses && <div style={{ fontSize: 10, color: '#E53935', fontWeight: 600, marginTop: 4 }}>EXHAUSTED</div>}
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Players & Registrations (merged) */}
        {activeTab === 'registrations' && (() => {
          const totalReg = t.registeredPlayers.length;
          const fee = t.entryFee || 0;
          const totalCollection = totalReg * fee;
          const regTabs = [
            { key: 'players', label: `Players (${totalReg})` },
            { key: 'add', label: <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}><Ico d={Icons.user} size={12} /> Add Existing</span> },
            { key: 'bulk', label: <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}><Ico d={Icons.plus} size={12} /> Bulk Add</span> },
            ...(!isDone && !t.bracketGenerated && totalReg >= 2 ? [{ key: 'seeds', label: <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}><Ico d={Icons.target} size={12} /> Seeds</span> }] : []),
          ];
          return (
            <div>
              <div className="org-reg-summary">
                <div className="org-reg-stat"><div className="org-reg-val">{totalReg}</div><div className="org-reg-lbl">PLAYERS</div></div>
                <div className="org-reg-divider" />
                <div className="org-reg-stat"><div className="org-reg-val">₹{fee.toLocaleString('en-IN')}</div><div className="org-reg-lbl">ENTRY FEE</div></div>
                <div className="org-reg-divider" />
                <div className="org-reg-stat"><div className="org-reg-val" style={{ color: '#43A047' }}>₹{totalCollection.toLocaleString('en-IN')}</div><div className="org-reg-lbl">COLLECTED</div></div>
              </div>

              {/* Sub tabs */}
              <div className="tabs" style={{ marginBottom: 16 }}>
                {regTabs.map(rt => (
                  <button key={rt.key} onClick={() => setRegSubTab(rt.key)} className={`tab ${regSubTab === rt.key ? 'tab-active' : ''}`}>{rt.label}</button>
                ))}
              </div>

              {/* Players tab */}
              {regSubTab === 'players' && (
                <>
                  <div style={{ position: 'relative', marginBottom: 14 }}>
                    <svg style={{ position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#8892A6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="11" cy="11" r="8"/>
                      <path d="m21 21-4.35-4.35"/>
                    </svg>
                    <input
                      className="form-input"
                      placeholder="Search players..."
                      value={playersTabSearch}
                      onChange={e => setPlayersTabSearch(e.target.value)}
                      style={{ width: '100%', padding: '12px 0 12px 32px', fontSize: 14, border: 0, borderBottom: '1px solid #E5E8EF', borderRadius: 0, background: 'transparent', transition: 'border-color 0.2s', outline: 0, boxShadow: 'none' }}
                      onFocus={e => { e.target.style.borderBottom = '2px solid #00205B'; e.target.style.outline = 'none'; e.target.style.boxShadow = 'none'; }}
                      onBlur={e => { e.target.style.borderBottom = '1px solid #E5E8EF'; e.target.style.boxShadow = 'none'; }}
                    />
                  </div>
                  <div className="org-p-grid">
                  {reg.length === 0 && <Card><p style={{ color: '#8892A6', textAlign: 'center' }}>No players registered yet</p></Card>}
                  {reg
                    .map((team, i) => ({ team, i, rpId: t.registeredPlayers[i] }))
                    .filter(({ team }) => {
                      const searchLower = playersTabSearch.toLowerCase();
                      return !searchLower || team.name.toLowerCase().includes(searchLower);
                    })
                    .map(({ team, i, rpId }) => {
                    const isSingle = rpId && !rpId.includes('+');
                    const seedEntry = (t.seeds || []).find(sd => sd.playerId === rpId);
                    return (
                      <div key={i}>
                        <Card 
                          style={{ padding: 12, display: 'flex', alignItems: 'center', gap: 10, cursor: isSingle ? 'pointer' : 'default' }}
                          onClick={() => isSingle && navigate(`/players/${rpId}`)}
                        >
                          <div className="org-p-num" style={seedEntry ? { background: '#00205B', color: '#BDFF00' } : {}}>
                            {seedEntry ? `S${seedEntry.seedNumber}` : i + 1}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <strong style={{ fontSize: 13, color: '#00205B' }}>{team.name}</strong>
                            {seedEntry && <div style={{ fontSize: 10, color: '#8892A6', fontWeight: 600 }}>Seed {seedEntry.seedNumber}</div>}
                          </div>
                          {fee > 0 && <span style={{ fontSize: 11, fontWeight: 700, color: '#43A047', flexShrink: 0 }}>₹{fee.toLocaleString('en-IN')}</span>}
                          {isSingle && <Ico d={Icons.eye} size={14} color="#B0B8C9" />}
                          {!isDone && !t.bracketGenerated && isSingle && (
                            <button onClick={e => { e.preventDefault(); e.stopPropagation(); if (!allPlayersLoaded) loadAllPlayers(); setReplacePlayerModal({ rpId, name: team.name }); }}
                              style={{ flexShrink: 0, background: 'none', border: 'none', color: '#1E88E5', cursor: 'pointer', fontSize: 16, lineHeight: 1, padding: 0, display: 'flex', alignItems: 'center' }}
                              title="Replace Player">
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="17 1 21 5 17 9"></polyline>
                                <path d="M3 11V9a4 4 0 0 1 4-4h14"></path>
                                <polyline points="7 23 3 19 7 15"></polyline>
                                <path d="M21 13v2a4 4 0 0 1-4 4H3"></path>
                              </svg>
                            </button>
                          )}
                          {!isDone && !t.bracketGenerated && (
                            <button onClick={e => { e.preventDefault(); e.stopPropagation(); setRemovePlayerModal({ rpId, name: team.name }); }}
                              style={{ flexShrink: 0, background: 'none', border: 'none', color: '#E53935', cursor: 'pointer', fontSize: 16, lineHeight: 1, padding: 0, display: 'flex', alignItems: 'center' }}><Ico d={Icons.xCircle} size={16} /></button>
                          )}
                        </Card>
                      </div>
                    );
                  })}
                </div>
                </>
              )}

              {/* Add Existing tab */}
              {regSubTab === 'add' && !isDone && (() => {
                console.log('Rendering Add Existing tab, allPlayers:', allPlayers.length, 'allPlayersLoaded:', allPlayersLoaded);
                
                if (loadingAllPlayers) {
                  return (
                    <Card>
                      <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                        <div style={{ fontSize: 32, marginBottom: 12 }}>⏳</div>
                        <p style={{ fontSize: 13, color: '#8892A6', margin: 0 }}>Loading players...</p>
                      </div>
                    </Card>
                  );
                }
                
                const unregistered = allPlayers
                  .filter(p => !t.registeredPlayers.some(rp => rp.includes(p.id)))
                  .filter(p => { const q = playerSearch.toLowerCase(); return !q || p.name.toLowerCase().includes(q) || (p.phone && p.phone.includes(q)); })
                  .sort((a, b) => a.name.localeCompare(b.name));
                return (
                  <Card>
                    <input className="form-input" style={{ marginBottom: 8 }} placeholder="Search by name or phone..." value={playerSearch} onChange={e => setPlayerSearch(e.target.value)} />
                    {addPlayerError && (
                      <div style={{ padding: '10px 12px', background: '#FFEBEE', border: '1.5px solid #E53935', borderRadius: 8, marginBottom: 10, fontSize: 12, color: '#E53935', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Ico d={Icons.xCircle} size={16} />
                        {addPlayerError}
                      </div>
                    )}
                    {isDoubles && selectedPlayers.length > 0 && (
                      <div style={{ fontSize: 11, color: '#E53935', fontWeight: 600, marginBottom: 6 }}>
                        {selectedPlayers.length === 1 ? 'Select 1 more player to form a team' : selectedPlayers.length > 2 ? 'Max 2 players per doubles team' : ''}
                      </div>
                    )}
                    {unregistered.length === 0 ? (
                      <p style={{ fontSize: 12, color: '#8892A6', margin: '8px 0' }}>{playerSearch ? 'No players match your search' : 'All players are already registered'}</p>
                    ) : (
                      <div style={{ maxHeight: 260, overflowY: 'auto', border: '1.5px solid #EEF1F6', borderRadius: 8, marginBottom: 10 }}>
                        {unregistered.map(p => {
                          const checked = selectedPlayers.includes(p.id);
                          const disabled = isDoubles && !checked && selectedPlayers.length >= 2;
                          return (
                            <label key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', borderBottom: '1px solid #F8F9FC', cursor: disabled ? 'not-allowed' : 'pointer', background: checked ? 'rgba(0,32,91,0.04)' : 'transparent', opacity: disabled ? 0.4 : 1 }}>
                              <input type="checkbox" checked={checked} disabled={disabled} onChange={() => setSelectedPlayers(prev => checked ? prev.filter(x => x !== p.id) : [...prev, p.id])} style={{ flexShrink: 0 }} />
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 13, fontWeight: checked ? 700 : 500, color: '#00205B', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</div>
                                {p.phone && <div style={{ fontSize: 10, color: '#8892A6' }}>{p.phone}</div>}
                              </div>
                              {checked && <span style={{ fontSize: 10, fontWeight: 700, color: '#00205B', background: '#EEF1F6', padding: '2px 6px', borderRadius: 4, flexShrink: 0, display: 'flex', alignItems: 'center' }}><Ico d={Icons.check} size={12} /></span>}
                            </label>
                          );
                        })}
                      </div>
                    )}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                      <span style={{ fontSize: 11, color: '#8892A6' }}>
                        {isDoubles ? `${selectedPlayers.length}/2 selected` : `${selectedPlayers.length} selected`}
                      </span>
                      <div style={{ display: 'flex', gap: 8 }}>
                        {selectedPlayers.length > 0 && <button onClick={() => setSelectedPlayers([])} style={{ padding: '7px 14px', fontSize: 11, fontWeight: 600, background: '#F8F9FC', color: '#5F6B80', border: '1.5px solid #D8DDE8', borderRadius: 7, cursor: 'pointer' }}>Clear</button>}
                        <button
                          disabled={isDoubles ? selectedPlayers.length !== 2 || addingPlayers : selectedPlayers.length === 0 || addingPlayers}
                          onClick={async () => {
                            setAddingPlayers(true);
                            setAddPlayerError('');
                            try {
                              if (isDoubles) {
                                await joinTournament(id, selectedPlayers[0], selectedPlayers[1]);
                              } else {
                                for (const pid of selectedPlayers) { await joinTournament(id, pid); }
                              }
                              await load();
                              setSelectedPlayers([]);
                              setPlayerSearch('');
                            } catch (err) {
                              console.error('Add player error:', err);
                              setAddPlayerError(err.response?.data?.message || err.message || 'Failed to add player(s). Please try again.');
                            } finally {
                              setAddingPlayers(false);
                            }
                          }}
                          style={{ ...s.genBtn, padding: '7px 16px', fontSize: 11, opacity: (isDoubles ? selectedPlayers.length !== 2 : selectedPlayers.length === 0) || addingPlayers ? 0.5 : 1 }}>
                          {addingPlayers ? 'Adding...' : isDoubles ? 'Register Team' : `Add ${selectedPlayers.length > 0 ? selectedPlayers.length : ''} Player${selectedPlayers.length !== 1 ? 's' : ''}`}
                        </button>
                      </div>
                    </div>
                  </Card>
                );
              })()}

              {/* Bulk Add tab */}
              {regSubTab === 'bulk' && !isDone && (
                <Card>
                  {isDoubles
                    ? <p style={{ fontSize: 11, color: '#8892A6', margin: '0 0 8px' }}>One team per line: <strong>Player1 Name|Phone1, Player2 Name|Phone2</strong></p>
                    : <p style={{ fontSize: 11, color: '#8892A6', margin: '0 0 8px' }}>One player per line: <strong>Name|Phone</strong>. Existing players (by phone) will be auto-registered.</p>
                  }
                  <textarea className="form-input" style={{ minHeight: 120, fontFamily: 'monospace', fontSize: 12 }}
                    placeholder={isDoubles ? `Rahul Sharma|9876543210, Priya Patel|9123456789\nAnand Kumar|9988776655, Suresh Rao|9876501234` : `Rahul Sharma|9876543210\nPriya Patel|9123456789`}
                    value={bulkText} onChange={e => { setBulkText(e.target.value); setBulkResult(null); }} />
                  <button onClick={handleBulkUpload} disabled={bulkLoading || !bulkText.trim()}
                    style={{ ...s.genBtn, marginTop: 8, padding: '8px 20px', fontSize: 11, opacity: bulkLoading || !bulkText.trim() ? 0.5 : 1 }}>
                    {bulkLoading ? 'Adding...' : `Add & Register ${isDoubles ? 'Teams' : 'Players'}`}
                  </button>
                  {bulkResult && (
                    <div style={{ marginTop: 10, fontSize: 12 }}>
                      {bulkResult.added.length > 0 && <div style={{ color: '#43A047', fontWeight: 600 }}>✓ {bulkResult.added.length} {isDoubles ? 'team(s)' : 'player(s)'} added & registered</div>}
                      {bulkResult.skipped.length > 0 && <div style={{ color: '#1E88E5', fontWeight: 600 }}>↩ {bulkResult.skipped.length} already registered</div>}
                      {bulkResult.errors.length > 0 && <div style={{ color: '#E53935', fontWeight: 600 }}>✕ {bulkResult.errors.length} failed</div>}
                    </div>
                  )}
                </Card>
              )}

              {/* Seeds tab */}
              {regSubTab === 'seeds' && !isDone && !t.bracketGenerated && totalReg >= 2 && (
                <Card>
                  <p style={{ fontSize: 11, color: '#8892A6', margin: '0 0 12px' }}>ITF rules: S1 → top, S2 → bottom, S3/S4 → opposite halves, S5–S8 → quarters.</p>
                  <div style={{ position: 'relative', marginBottom: 12 }}>
                    <svg style={{ position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#8892A6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="11" cy="11" r="8"/>
                      <path d="m21 21-4.35-4.35"/>
                    </svg>
                    <input
                      className="form-input"
                      placeholder="Search players..."
                      value={seedsTabSearch}
                      onChange={e => setSeedsTabSearch(e.target.value)}
                      style={{ width: '100%', padding: '12px 0 12px 32px', fontSize: 14, border: 0, borderBottom: '1px solid #E5E8EF', borderRadius: 0, background: 'transparent', transition: 'border-color 0.2s', outline: 0, boxShadow: 'none' }}
                      onFocus={e => { e.target.style.borderBottom = '2px solid #00205B'; e.target.style.outline = 'none'; e.target.style.boxShadow = 'none'; }}
                      onBlur={e => { e.target.style.borderBottom = '1px solid #E5E8EF'; e.target.style.boxShadow = 'none'; }}
                    />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {t.registeredPlayers
                      .map(rpId => ({ rpId, team: resolveTeam(rpId, players) }))
                      .filter(({ team }) => {
                        const searchLower = seedsTabSearch.toLowerCase();
                        return !searchLower || team.name.toLowerCase().includes(searchLower);
                      })
                      .map(({ rpId, team }) => {
                      const existing = (t.seeds || []).find(sd => sd.playerId === rpId);
                      return (
                        <div key={rpId} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          {existing && <span style={{ fontSize: 10, fontWeight: 800, color: '#BDFF00', background: '#00205B', padding: '2px 7px', borderRadius: 4, flexShrink: 0 }}>S{existing.seedNumber}</span>}
                          <span style={{ flex: 1, fontSize: 13, color: '#00205B', fontWeight: existing ? 700 : 400 }}>{team.name}</span>
                          <select style={{ ...s.fInput, width: 110, padding: '6px 10px' }} value={existing?.seedNumber || ''}
                            onChange={async e => { const val = e.target.value; const updated = [...(t.seeds || []).filter(sd => sd.playerId !== rpId)]; if (val) updated.push({ playerId: rpId, seedNumber: parseInt(val) }); await setTournamentSeeds(t.id, updated); await load(); }}>
                            <option value="">No seed</option>
                            {[1,2,3,4,5,6,7,8,9,10,11,12,13,14,15,16].map(n => { const taken = (t.seeds || []).find(sd => sd.seedNumber === n && sd.playerId !== rpId); return <option key={n} value={n} disabled={!!taken}>Seed {n}{taken ? ' ✗' : ''}</option>; })}
                          </select>
                        </div>
                      );
                    })}
                  </div>
                </Card>
              )}
            </div>
          );
        })()}
      </div>

      <ConfirmModal
        isOpen={completeModal}
        onClose={() => setCompleteModal(false)}
        onConfirm={async () => {
          const res = await completeTournament(id);
          if (res?.pointsAwarded) setPointsResult(res.pointsAwarded);
          await load();
        }}
        title="Complete Tournament"
        message={isAdvanced ? "Mark this tournament as completed? This will award ranking points to all players based on their performance and lock all matches." : "Mark this tournament as completed? This will lock all matches. Upgrade to Advanced for ranking points & AI summary."}
        confirmText={isAdvanced ? "Complete & Award Points" : "Mark as Complete"}
        confirmColor="#43A047"
        icon={<Ico d={Icons.check} size={24} color="#43A047" />}
      />

      {/* Points Awarded Modal */}
      <Modal isOpen={!!pointsResult} onClose={() => setPointsResult(null)} title="🏆 Points Awarded">
        {pointsResult && (
          <div>
            <p style={{ fontSize: 12, color: '#5F6B80', marginBottom: 12 }}>Tournament completed! Ranking points awarded:</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 300, overflowY: 'auto' }}>
              {Object.entries(pointsResult)
                .sort(([,a], [,b]) => b - a)
                .map(([pid, pts]) => {
                  const team = resolveTeam(pid, players);
                  return (
                    <div key={pid} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', background: '#F8F9FC', borderRadius: 8 }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: '#00205B' }}>{team.name}</span>
                      <span style={{ fontSize: 13, fontWeight: 800, color: '#43A047', fontFamily: "'Outfit', sans-serif" }}>+{pts} pts</span>
                    </div>
                  );
                })}
            </div>
            <button onClick={() => setPointsResult(null)} style={{ width: '100%', marginTop: 14, padding: '10px', background: '#00205B', color: '#BDFF00', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 12, cursor: 'pointer', fontFamily: "'Outfit', sans-serif" }}>Done</button>
          </div>
        )}
      </Modal>

      <ConfirmModal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={() => {
          if (deleteConfirm?.type === 'announcement') {
            deleteAnnouncement(id, deleteConfirm.id);
          } else if (deleteConfirm?.type === 'coupon') {
            deleteCoupon(id, deleteConfirm.id);
          }
        }}
        title={`Delete ${deleteConfirm?.type === 'announcement' ? 'Announcement' : 'Coupon'}`}
        message={`Are you sure you want to delete this ${deleteConfirm?.type}? This action cannot be undone.`}
        confirmText="Delete"
        icon={<Ico d={Icons.trash} size={24} color="#E53935" />}
      />

      <Modal isOpen={!!removePlayerModal} onClose={() => setRemovePlayerModal(null)} title="Remove Player">
        {removePlayerModal && (
          <div>
            <div style={{ textAlign: 'center', marginBottom: 20 }}>
              <div style={{ fontSize: 36, marginBottom: 10, display: 'flex', justifyContent: 'center' }}><Ico d={Icons.xCircle} size={36} color="#E53935" /></div>
              <p style={{ fontSize: 14, fontWeight: 600, color: '#00205B', margin: '0 0 6px' }}>{removePlayerModal.name}</p>
              <p style={{ fontSize: 13, color: '#5F6B80', margin: 0 }}>Are you sure you want to remove this player from the tournament? This cannot be undone.</p>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => setRemovePlayerModal(null)} style={{ flex: 1, padding: '10px 0', background: '#F8F9FC', color: '#5F6B80', border: '1.5px solid #D8DDE8', borderRadius: 8, fontWeight: 600, cursor: 'pointer', fontSize: 13 }}>Cancel</button>
              <button
                onClick={async () => {
                  const pid = removePlayerModal.rpId.includes('+') ? removePlayerModal.rpId.split('+')[0] : removePlayerModal.rpId;
                  await leaveTournament(id, pid);
                  await load();
                  setRemovePlayerModal(null);
                }}
                style={{ flex: 1, padding: '10px 0', background: '#E53935', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: 13 }}
              >Remove Player</button>
            </div>
          </div>
        )}
      </Modal>

      <Modal isOpen={!!replacePlayerModal} onClose={() => { setReplacePlayerModal(null); setReplaceSearch(''); }} title="Replace Player">
        {replacePlayerModal && (
          <div>
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#00205B', marginBottom: 8 }}>Current Player:</div>
              <div style={{ padding: '10px 12px', background: '#FFF3E0', border: '1.5px solid #FFB74D', borderRadius: 8, fontSize: 13, color: '#00205B', fontWeight: 600 }}>{replacePlayerModal.name}</div>
            </div>
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#00205B', marginBottom: 8 }}>Replace with:</div>
              <input className="form-input" style={{ marginBottom: 8 }} placeholder="Search by name or phone..." value={replaceSearch} onChange={e => setReplaceSearch(e.target.value)} />
            </div>
            {loadingAllPlayers ? (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <div style={{ fontSize: 24, marginBottom: 8 }}>⏳</div>
                <p style={{ fontSize: 12, color: '#8892A6', margin: 0 }}>Loading players...</p>
              </div>
            ) : (() => {
              const availablePlayers = allPlayers
                .filter(p => !t.registeredPlayers.includes(p.id) && p.id !== replacePlayerModal.rpId)
                .filter(p => { const q = replaceSearch.toLowerCase(); return !q || p.name.toLowerCase().includes(q) || (p.phone && p.phone.includes(q)); })
                .sort((a, b) => a.name.localeCompare(b.name));
              
              return availablePlayers.length === 0 ? (
                <p style={{ fontSize: 12, color: '#8892A6', textAlign: 'center', padding: '20px 0' }}>{replaceSearch ? 'No players match your search' : 'No available players to replace with'}</p>
              ) : (
                <div style={{ maxHeight: 240, overflowY: 'auto', border: '1.5px solid #EEF1F6', borderRadius: 8, marginBottom: 12 }}>
                  {availablePlayers.map(p => (
                    <div key={p.id} onClick={() => !replacingPlayer && handleReplacePlayer(p.id)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderBottom: '1px solid #F8F9FC', cursor: replacingPlayer ? 'not-allowed' : 'pointer', opacity: replacingPlayer ? 0.5 : 1, transition: 'background 0.2s' }} onMouseEnter={e => !replacingPlayer && (e.currentTarget.style.background = '#F8F9FC')} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <PlayerAvatar name={p.name} photo={p.photo} size={32} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#00205B', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</div>
                        {p.phone && <div style={{ fontSize: 11, color: '#8892A6' }}>{p.phone}</div>}
                      </div>
                      <Ico d={Icons.chevron} size={14} color="#1E88E5" />
                    </div>
                  ))}
                </div>
              );
            })()}
            <button onClick={() => { setReplacePlayerModal(null); setReplaceSearch(''); }} disabled={replacingPlayer} style={{ width: '100%', padding: '10px 0', background: '#F8F9FC', color: '#5F6B80', border: '1.5px solid #D8DDE8', borderRadius: 8, fontWeight: 600, cursor: replacingPlayer ? 'not-allowed' : 'pointer', fontSize: 13, opacity: replacingPlayer ? 0.5 : 1 }}>Cancel</button>
          </div>
        )}
      </Modal>

      <Modal isOpen={resetModal} onClose={() => setResetModal(false)} title="Reset Tournament">
        <div>
          <p style={{ fontSize: 13, color: '#5F6B80', marginBottom: 16 }}>Choose what to reset. This cannot be undone.</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 20 }}>
            {[
              { key: 'removeMatches', label: 'Delete all matches', desc: 'Removes the entire draw/fixtures' },
              { key: 'resetBracketGeneration', label: 'Reset bracket generation', desc: 'Allows regenerating the draw' },
              { key: 'resetSeeds', label: 'Clear seed assignments', desc: 'Removes all player seeds' },
              { key: 'removePlayers', label: 'Remove all players', desc: 'Clears all registrations' },
            ].map(({ key, label, desc }) => (
              <label key={key} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
                <input type="checkbox" checked={resetFlags[key]} onChange={e => setResetFlags(p => ({ ...p, [key]: e.target.checked }))} style={{ marginTop: 2, flexShrink: 0 }} />
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#00205B' }}>{label}</div>
                  <div style={{ fontSize: 11, color: '#8892A6' }}>{desc}</div>
                </div>
              </label>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => setResetModal(false)} style={{ flex: 1, padding: '10px 0', background: '#F8F9FC', color: '#5F6B80', border: '1.5px solid #D8DDE8', borderRadius: 8, fontWeight: 600, cursor: 'pointer', fontSize: 12 }}>Cancel</button>
            <button
              onClick={async () => {
                await resetTournament(id, resetFlags);
                setResetModal(false);
                await load();
              }}
              style={{ flex: 1, padding: '10px 0', background: '#E53935', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: 12 }}
            >Confirm Reset</button>
          </div>
        </div>
      </Modal>

      <Modal isOpen={!!scoreModal} onClose={() => setScoreModal(null)} title={scoreModal?.player1 && scoreModal?.player2 ? 'Update Match' : 'Set Schedule'}>
        {scoreModal && (() => {
          const t1 = scoreModal.player1 ? resolveTeam(scoreModal.player1, players) : { name: 'TBD' };
          const t2 = scoreModal.player2 ? resolveTeam(scoreModal.player2, players) : { name: 'TBD' };
          return (
            <MatchUpdateForm
              match={scoreModal}
              t1={t1}
              t2={t2}
              scoreForm={scoreForm}
              setScoreForm={setScoreForm}
              addSet={addSet}
              removeSet={removeSet}
              decSet={decSet}
              incSet={incSet}
              updSet={updSet}
              saveScore={saveScore}
              savingScore={savingScore}
              scoreError={scoreError}
            />
          );
        })()}
      </Modal>
    </div>
  );
}

const s = {
  // Hero meta (white text on gradient — unique to this page)
  liveTag: { fontSize: 10, fontWeight: 800, color: '#E53935', background: 'rgba(229,57,53,0.1)', padding: '2px 8px', borderRadius: 4, letterSpacing: 0.5 },

  genBtn: { padding: '10px 20px', background: '#00205B', color: '#BDFF00', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: 12, letterSpacing: 0.5, fontFamily: "'Outfit', sans-serif" },
  completeBtn: { padding: '8px 16px', background: '#43A047', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: 11, display: 'flex', alignItems: 'center', gap: 4 },
  resetBtn: { padding: '8px 16px', background: 'var(--gray-50)', color: '#E53935', border: '1.5px solid #FFCDD2', borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: 11 },

  // Score button
  scoreBtn: { width: '100%', padding: 8, marginTop: 8, background: '#00205B', color: '#BDFF00', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 700, fontSize: 11, letterSpacing: 0.5, fontFamily: "'Outfit', sans-serif" },
  fInput: { padding: '10px 12px', border: '1.5px solid #D8DDE8', borderRadius: 8, fontSize: 13, outline: 'none', background: '#F8F9FC', color: '#2A3244', boxSizing: 'border-box' },
};
